import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { KieneBackendApiService } from '../../../../../services/backend-api/kiene-backend-api.service';
import { Betriebskontakt } from '../../../../stammdaten/betriebskontakte/betriebskontakt';
import { Haendler } from '../../../../stammdaten/haendler/haendler';
import { Abteil } from '../../gruppe-tier-abteil-filter/gruppe-tier-abteil-filter';
import { GruppeTierEinstalldatum } from '../../gruppe-tier-einstalldatum-filter/gruppe-tier-einstalldatum';
import { HerkunftsbetriebFilter } from '../../gruppe-tier-herkunft-filter/gruppe-tier-herkunft-filter';
import { TierFilter } from './tier-filter';
import { TierFilterSettings } from './tier-filter-settings';
import { TierGeschlecht } from '../../../classes/tier-geschlecht';
import { TierRasse } from '../../../classes/tier-rasse';
import { Tierliste } from '../../../classes/tierliste';
import { Tierreklamation } from '../../../classes/tierreklamation';

/** Speicher key for tier filter on group-details only (not Ausstallen/Abgang). */
export const GRUPPE_DETAILS_TIER_FILTER_SPEICHER_KEY = 'gruppe_details_tier_filter';

interface TierFilterSettingsPersisted {
    saveFilterEnabled?: boolean;
    filterKey: string | null;
    filterValue: string | null;
    abteile: string[];
    tierreklamationIds: number[];
    geschlechtIds: number[];
    rasseIds: number[];
    tierlisteIds: number[];
    viehhaendlerIds: number[];
    betriebskontaktKundeIds: number[];
    herkunftKundeIds: number[];
    einstalldatum: string[];
}

export interface TierFilterPersistenceState {
    settings: TierFilterSettings;
    saveFilterEnabled: boolean;
}

export class TierFilterSettingsPersistence {
    static load(api: KieneBackendApiService): Observable<TierFilterPersistenceState | null> {
        return api.getSpeicher(GRUPPE_DETAILS_TIER_FILTER_SPEICHER_KEY).pipe(
            map((raw: TierFilterSettingsPersisted | null) => {
                if (!raw) {
                    return null;
                }
                const settings = this.fromPersisted(raw);
                const saveFilterEnabled =
                    raw.saveFilterEnabled === true ||
                    (raw.saveFilterEnabled === undefined && settings.isFilterActive());
                return { settings, saveFilterEnabled };
            }),
            catchError(() => of(null)),
        );
    }

    static save(
        api: KieneBackendApiService,
        settings: TierFilterSettings,
        saveFilterEnabled: boolean,
    ): Observable<boolean> {
        if (!saveFilterEnabled) {
            return api.setSpeicher(GRUPPE_DETAILS_TIER_FILTER_SPEICHER_KEY, this.emptyPersisted(false));
        }
        return api.setSpeicher(GRUPPE_DETAILS_TIER_FILTER_SPEICHER_KEY, {
            ...this.toPersisted(settings),
            saveFilterEnabled: true,
        });
    }

    static toPersisted(settings: TierFilterSettings): TierFilterSettingsPersisted {
        return {
            saveFilterEnabled: true,
            filterKey: settings.filter?.key ?? null,
            filterValue: settings.filter?.value != null ? String(settings.filter.value) : null,
            abteile: (settings.abteile || []).map(a => a.abteil).filter(Boolean),
            tierreklamationIds: (settings.reklamationen || []).map(r => r.tierreklamation_id).filter(id => id > 0),
            geschlechtIds: (settings.geschlechter || []).map(g => g.geschlecht_id).filter(id => id > 0),
            rasseIds: (settings.rassen || []).map(r => r.rasse_id).filter(id => id > 0),
            tierlisteIds: (settings.abgangslisten || []).map(l => l.tierliste_id).filter(id => id > 0),
            viehhaendlerIds: (settings.viehhaendler || []).map(h => h.viehhaendler_id).filter(id => id > 0),
            betriebskontaktKundeIds: (settings.betriebskontakte || []).map(k => k.kunde_id).filter(id => id > 0),
            herkunftKundeIds: (settings.herkunftsbetriebe || [])
                .map(h => h.herkunft_kunde_id)
                .filter(id => id > 0),
            einstalldatum: (settings.einstalldatum || [])
                .map(d => this.formatDate(d?.einstalldatum))
                .filter((d): d is string => !!d),
        };
    }

    static fromPersisted(raw: TierFilterSettingsPersisted): TierFilterSettings {
        const settings = new TierFilterSettings();
        settings.filter = this.resolveTierFilter(raw.filterKey, raw.filterValue);
        settings.abteile = (raw.abteile || []).map(abteil => {
            const a = new Abteil();
            a.abteil = abteil;
            return a;
        });
        settings.reklamationen = (raw.tierreklamationIds || []).map(id => new Tierreklamation(id));
        settings.geschlechter = (raw.geschlechtIds || []).map(id => {
            const g = new TierGeschlecht();
            g.geschlecht_id = id;
            return g;
        });
        settings.rassen = (raw.rasseIds || []).map(id => {
            const r = new TierRasse();
            r.rasse_id = id;
            return r;
        });
        settings.abgangslisten = (raw.tierlisteIds || []).map(id => {
            const l = new Tierliste();
            l.tierliste_id = id;
            return l;
        });
        settings.viehhaendler = (raw.viehhaendlerIds || []).map(id => {
            const h = new Haendler();
            h.viehhaendler_id = id;
            return h;
        });
        settings.betriebskontakte = (raw.betriebskontaktKundeIds || []).map(
            (kundeId): Betriebskontakt => ({
                kontakt_id: kundeId,
                benutzer_id: 0,
                kunde_id: kundeId,
                favorit: 0,
                ist_kunde: 0,
                bezeichnung: '',
                vvvo: '',
                bezeichnung_final: '',
            }),
        );
        settings.herkunftsbetriebe = (raw.herkunftKundeIds || []).map(id => {
            const h = new HerkunftsbetriebFilter();
            h.herkunft_kunde_id = id;
            return h;
        });
        settings.einstalldatum = (raw.einstalldatum || []).map(dateStr => {
            const e = new GruppeTierEinstalldatum();
            e.einstalldatum = new Date(dateStr);
            return e;
        });
        // Group-details persistence always includes these filter panels.
        settings.showBetriebskontakte = true;
        settings.showHerkunftsbetriebe = true;
        return settings;
    }

    private static emptyPersisted(saveFilterEnabled: boolean): TierFilterSettingsPersisted {
        return {
            saveFilterEnabled,
            filterKey: null,
            filterValue: null,
            abteile: [],
            tierreklamationIds: [],
            geschlechtIds: [],
            rasseIds: [],
            tierlisteIds: [],
            viehhaendlerIds: [],
            betriebskontaktKundeIds: [],
            herkunftKundeIds: [],
            einstalldatum: [],
        };
    }

    private static resolveTierFilter(key: string | null, value: string | null): TierFilter {
        if (!key) {
            return TierFilter.ALLE_TIERE;
        }
        const match = TierFilter.getAvailableFilter().find(
            f => f.key === key && String(f.value) === String(value),
        );
        return match ?? new TierFilter('', key, value);
    }

    private static formatDate(value: Date | string | undefined): string | null {
        if (!value) {
            return null;
        }
        const d = value instanceof Date ? value : new Date(value);
        if (isNaN(d.getTime())) {
            return null;
        }
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
}
